import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { forceVisible } from 'react-lazyload';
import { Redirect, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import Carousel, { Slide } from '../../library/carousel/carousel';
import Link from '../../library/link/link';
import Page, { Content, PageTitle, Section } from '../../library/page/page';
import Tabs, { Tab } from '../../library/tabs/tabs';
import Typography from '../../library/typography/typography';
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs';
import LazyLoad from '../../components/lazy-load/lazy-load';
import PopularTimes from '../../components/popular-times/popular-times';
import ReadMore from '../../components/read-more/read-more';
import ReviewOverview from '../../components/review-overview/review-overview';
import StaticMap from '../../components/static-map/static-map';
import StoreQuickView from '../../components/store-quick-view/store-quick-view';
import AdditionalStoreServices from './components/additional-store-services/additional-store-services';
import MoreStores from './components/more-stores/more-stores';
import StoreHolidayMessage from './components/store-holiday-message/store-holiday-message';
import StoreHours from './components/store-hours/store-hours';
import StoreHoursMessage from './components/store-hours-message/store-hours-message';
import StorePromos from './components/store-promos/store-promos';
import StoreReviews from './components/store-reviews/store-reviews';
import StoreServices from './components/store-services/store-services';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import { UserSolidIcon } from '../../shared/icons/icons';

import {
  GET_ADDITIONAL_SERVICES_BY_STORE_CODE,
  GET_STORE_CMS_PAGE,
  GET_STORE_DETAILS
} from './graphql/store-detail-queries';

import {
  useDataLayer,
  useMediaQuery,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  useOptimisticData,
  useQuery,
  useScrollTo
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getCmsComponentsInSlots } from '../../shared/utils/cms/cms';
import { formatToTitleCase } from '../../shared/utils/format/format';
import { metaTags as getMetaTags } from '../../shared/utils/page/page';
import {
  getStoreDataLayerValues,
  getStoreDetailsUrl,
  isDayClosed,
  isPartialHoliday
} from '../../shared/utils/store/store';
import { getCurrentMonthOfYear } from '../../shared/utils/time/time';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { getStoreLD } from './utils/store-detail-utils';

import {
  PAGE_CATEGORY,
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE
} from '../../shared/constants/data-layer';
import {
  BASE_STORE_TYPES,
  CANCEL_STORE_CHANGE
} from '../../shared/constants/store';

import './store-detail-page.scss';

export const DEFAULT_HEADING = 'Store Details';

export function StoreDetailPage() {
  const [isChangingStore, setIsChangingStore] = useState(false);
  const { myStore, setStore } = useMyStore();
  const navigateTo = useNavigateTo();
  const scrollToReviews = useScrollTo('#read-reviews', {
    offset: 100
  });
  const params = useParams();
  const { storeCode = '' } = params || {};
  const { updateDataLayer } = useDataLayer();
  const optimisticData = useOptimisticData();
  const previousStoreCodeRef = useRef();
  const { isFleetAccount } = useMyAccount();
  const { isBopisFleetEnabled = false, pitPass: isPitPassStore = false } =
    myStore || {};

  async function setStoreAndCart() {
    const resp = await setStore(storeCode);

    if (resp === CANCEL_STORE_CHANGE) {
      navigateTo(getStoreDetailsUrl(myStore.address, myStore.code));
    }
    setIsChangingStore(false);
  }

  if (
    !window.history.state &&
    myStore &&
    myStore.code !== storeCode &&
    !isChangingStore
  ) {
    setIsChangingStore(true);
    setStoreAndCart();
  }

  const {
    data,
    error,
    isLoading: isLoading
  } = useQuery(GET_STORE_DETAILS, {
    errorPolicy: 'all',
    returnPartialData: true,
    variables: {
      month: getCurrentMonthOfYear(),
      sourceLatitude: myStore?.geoPoint?.latitude,
      sourceLongitude: myStore?.geoPoint?.longitude,
      storeCode
    }
  });

  const {
    data: additionalServicesData,
    isLoading: isLoadingAdditionalServices
  } = useQuery(GET_ADDITIONAL_SERVICES_BY_STORE_CODE, {
    skip: !storeCode,
    variables: {
      storeCode
    }
  });
  const { additionalServices = [] } = additionalServicesData?.store || {};
  const hasAdditionalServices = isArrayWithLength(additionalServices);
  const { data: cmsData, isLoading: isCMSDataLoading } = useQuery(
    GET_STORE_CMS_PAGE,
    {
      errorPolicy: 'all',
      variables: {
        id: window.location.pathname
      }
    }
  );

  const optimisticStoreData = optimisticData.read('store');
  const { services = [], store } = data || {};
  const { byCode } = store || {};
  const {
    contentSlots,
    documentTitle: cmsDocumentTitle,
    metaTags
  } = cmsData?.cms?.page || {};
  const hasServices = isArrayWithLength(services);

  const {
    address,
    baseStore,
    code = '',
    currentTime,
    distance,
    features,
    geoPoint,
    largeImages,
    legacyStoreCode,
    managerName,
    mediumImages,
    popularTimes,
    rating,
    reviewsSummary = [],
    smallImages,
    storeContent,
    weekDays = []
  } = byCode || optimisticStoreData || {};

  const storeType =
    baseStore === BASE_STORE_TYPES.DT ? 'Discount' : "America's";

  const breakPoint = useMediaQuery(checkBreakpoint);
  const mapHeight = breakPoint?.isMinSm ? 600 : 280;
  const mapWidth = breakPoint?.isMinSm ? 1145 : 650;
  const dateFormat = 'MM/DD/YYYY';
  const [selectedTabIndex, setSelectedTabIndex] = useState(1);
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), dateFormat)
  );
  const [pageHeading, setPageHeading] = useState(DEFAULT_HEADING);
  const [overviewHeading, setOverviewHeading] = useState('');
  const [readingMore, setReadingMore] = useState(false);
  const [storeDetailsDataLayerValues, setStoreDetailsDataLayerValues] =
    useState(null);
  const [storeMapHeight, setStoreMapHeight] = useState(mapHeight);
  const [nearByMapWidth, setNearByMapWidth] = useState(mapWidth);
  const isMyStore = !window.isPreRendering && myStore?.code === code;

  const breadcrumbs = [
    {
      name: 'Home',
      url: '/'
    },
    {
      name: 'Store Locator',
      url: '/store-locator'
    }
  ];

  if (address?.line1) {
    breadcrumbs.push({
      name: ` ${storeType} Tire ${formatToTitleCase(address.line1)}`
    });
  }

  const popularDates =
    Array.isArray(weekDays) &&
    weekDays.map(d => format(new Date(d?.formattedDate), dateFormat));
  const currentIdx =
    Array.isArray(weekDays) &&
    weekDays.findIndex(
      d => format(new Date(d?.formattedDate), dateFormat) === selectedDate
    );
  const selectedDayData = weekDays[currentIdx];
  const showClosedMessage =
    selectedDayData?.closed || isDayClosed(selectedDayData);
  const showPartialHoursMessage = isPartialHoliday(selectedDayData);
  const averageAnswerRatings = [
    {
      code: 'overall',
      label: 'Overall Rating',
      value: rating?.rating
    },
    {
      code: 'knowledge',
      label: 'Employee Knowledge/Friendliness',
      value: rating?.knowledgeRating
    },
    {
      code: 'cleanliness',
      label: 'Store Cleanliness',
      value: rating?.cleanlinessRating
    }
  ];

  function getDocumentTitle() {
    let documentTitle = '';

    if (cmsDocumentTitle) {
      documentTitle = cmsDocumentTitle;
    } else if (!isCMSDataLoading && address && baseStore) {
      const { postalCode, region, town } = address;
      const zip = typeof postalCode === 'string' ? postalCode.slice(0, -5) : '';
      const city = town ? town.split(' ').map(capitalize).join(' ') : '';
      documentTitle = `${storeType} Tire Store | ${city}, ${region?.isocodeShort} | ${zip} | Tire Shop Near Me`;
    }

    return documentTitle;
  }

  function getCanonicalUrl() {
    const { pathname, protocol } = window.location;
    let host = process.env.__DT_HOST__;

    if (baseStore === BASE_STORE_TYPES.AT) {
      host = process.env.__AT_HOST__;
    }

    return `${protocol}//${host}${pathname}`;
  }

  function getStoreImages() {
    let storeImages = [];

    if (window.isPreRendering || isLoading) {
      storeImages = [{ url: null }, { url: null }, { url: null }];
    } else if (Array.isArray(largeImages) && breakPoint.isMinLg) {
      storeImages = largeImages;
    } else if (Array.isArray(mediumImages) && breakPoint.isMinSm) {
      storeImages = mediumImages;
    } else {
      storeImages = smallImages;
    }

    return storeImages;
  }

  function checkBreakpoint(breakPoint) {
    if (breakPoint.isMinSm) {
      setNearByMapWidth(1145);
      setStoreMapHeight(600);
    } else {
      setNearByMapWidth(650);
      setStoreMapHeight(280);
    }
  }

  const storeImages = getStoreImages();
  const documentTitle = getDocumentTitle();
  const storeLD = getStoreLD(address, rating);

  useEffect(() => {
    if (
      location.hash === '#read-reviews' &&
      !isLoading &&
      !isCMSDataLoading &&
      !isLoadingAdditionalServices &&
      code &&
      code !== previousStoreCodeRef.current
    ) {
      forceVisible();
      scrollToReviews();
      previousStoreCodeRef.current = code;
    }
  }, [
    code,
    isLoading,
    isCMSDataLoading,
    isLoadingAdditionalServices,
    scrollToReviews
  ]);

  useEffect(() => {
    let pageHeading = DEFAULT_HEADING;
    let overviewHeading = 'Store Overview';

    if (window.isPreRendering) return;

    if (cmsData && (!isCMSDataLoading || !isArrayWithLength(contentSlots))) {
      setPageHeading(pageHeading);
      setOverviewHeading(overviewHeading);
    }

    if (isArrayWithLength(contentSlots)) {
      getCmsComponentsInSlots(contentSlots, cmsComponent => {
        if (cmsComponent && cmsComponent.html) {
          if (cmsComponent.name === 'StoreHeading') {
            pageHeading = cmsComponent.html;
          } else if (cmsComponent.name === 'StoreOverviewHeading') {
            overviewHeading = cmsComponent.html;
          }
        }
      });

      setPageHeading(pageHeading);
      setOverviewHeading(overviewHeading);
    }
  }, [cmsData, contentSlots, isCMSDataLoading]);

  useEffect(() => {
    if (!isCMSDataLoading && documentTitle) {
      updateDataLayer({
        [PAGE_CATEGORY]: 'store detail',
        [PAGE_NAME]: 'store detail',
        [PAGE_SECTION_NAME]: documentTitle,
        [PAGE_SUBCATEGORY]: 'store detail',
        [PAGE_TYPE]: 'store_detail'
      });
    }
  }, [documentTitle, isCMSDataLoading, updateDataLayer]);

  useEffect(() => {
    if ((byCode || optimisticStoreData) && address) {
      setStoreDetailsDataLayerValues(
        getStoreDataLayerValues(byCode || optimisticStoreData)
      );
    }
  }, [address, optimisticStoreData, byCode]);

  if (error && !window.isPreRendering) {
    throw error;
  } else if (!store?.byCode && !isLoading && !window.isPreRendering) {
    return <Redirect to="/store-locator" />;
  }

  return (
    <DefaultLayout hideMobileStoreHeader>
      <Page>
        {!window.isPreRendering && (
          <Helmet>
            {documentTitle && <title>{documentTitle}</title>}
            {getMetaTags(metaTags)}
            {storeLD && <script type="application/ld+json">{storeLD}</script>}
            <link href={getCanonicalUrl()} rel="canonical" />
            {((baseStore === BASE_STORE_TYPES.DT && window.isAT) ||
              (baseStore === BASE_STORE_TYPES.AT && window.isDT)) && (
              <meta content="noindex" name="robots" />
            )}
          </Helmet>
        )}
        <Breadcrumbs crumbs={breadcrumbs} />
        <PageTitle
          showDivider
          title={
            <span
              dangerouslySetInnerHTML={{
                __html: pageHeading
              }}
            />
          }
        />
        <Content showFrame>
          <div styleName="quick-view">
            <div styleName="store-images">
              {Array.isArray(storeImages) && (
                <Carousel hasFullWidthSlides showIndicators>
                  {storeImages.map((image, idx) => (
                    <Slide key={`si-${idx}`} shouldLazyLoad>
                      <img
                        aria-label={image?.altText}
                        style={{ backgroundImage: `url(${image?.url})` }}
                        styleName="slide"
                      />
                    </Slide>
                  ))}
                </Carousel>
              )}
              {isPitPassStore && (
                <Button
                  href="/pitpass"
                  isSecondary
                  styleName="pitpass-learn-more"
                >
                  Learn More
                </Button>
              )}
            </div>
            <div styleName="quick-info">
              <StoreQuickView
                address={window.isPreRendering ? undefined : address}
                baseStore={baseStore}
                distance={
                  window.isPreRendering || isMyStore ? undefined : distance
                }
                features={window.isPreRendering ? undefined : features}
                geoPoint={window.isPreRendering ? undefined : geoPoint}
                isMyStore={isMyStore}
                isPitPassStore={isPitPassStore}
                legacyStoreCode={legacyStoreCode}
                onCallStore={() =>
                  trackAction(
                    'store_detail_call_store',
                    storeDetailsDataLayerValues
                  )
                }
                onGetDirectionsClick={() =>
                  trackAction(
                    'store_detail_directions',
                    storeDetailsDataLayerValues
                  )
                }
                onReadReview={() => {
                  forceVisible();
                  scrollToReviews();
                }}
                onReadReviewClick={() =>
                  trackAction(
                    'store_detail_read_reviews',
                    storeDetailsDataLayerValues
                  )
                }
                onScheduleAppointmentClick={() =>
                  trackAction(
                    'store_detail_schedule_appointment',
                    storeDetailsDataLayerValues
                  )
                }
                onSetStoreClick={() => {
                  trackAction(
                    'store_detail_make_this_my_store',
                    storeDetailsDataLayerValues
                  );
                  trackAction(
                    'store_detail_distance_between_stores',
                    storeDetailsDataLayerValues
                  );
                }}
                onShopProductsClick={() =>
                  trackAction(
                    'store_detail_shop_products',
                    storeDetailsDataLayerValues
                  )
                }
                rating={window.isPreRendering ? null : rating}
                showAltDirectionsCTA
                showAltScheduleAppointmentCTA={!isMyStore}
                showAltShareStoreCTA
                showAppointmentCTA={isMyStore}
                showFleetBopisDisabledMessage={
                  process.env.__FF_FLEET_ACCOUNT__ === 'true' &&
                  isFleetAccount &&
                  !isBopisFleetEnabled
                }
                showMakeMyStoreCTA={!isMyStore}
                showShopProductsCTA={isMyStore && !window.isPreRendering}
                storeCode={storeCode}
              />
            </div>
          </div>
          <div styleName="store-times">
            <div styleName="hours-services">
              <Tabs>
                <Tab
                  isSelected={selectedTabIndex === 1}
                  onClick={() => setSelectedTabIndex(1)}
                >
                  Store Hours
                </Tab>
                <Tab
                  isSelected={selectedTabIndex === 2}
                  onClick={() => setSelectedTabIndex(2)}
                >
                  Store Services
                </Tab>
              </Tabs>
              <div role="tabpanel" styleName="tab-container">
                {!window.isPreRendering && (
                  <>
                    <StoreHoursMessage
                      currentTime={currentTime}
                      weekDays={weekDays}
                    />
                    {selectedTabIndex === 1 && Array.isArray(weekDays) && (
                      <StoreHours
                        selectedDate={selectedDate}
                        weekDays={weekDays}
                      />
                    )}
                    {selectedTabIndex === 2 && (
                      <>
                        {hasServices && (
                          <StoreServices servicesList={services} />
                        )}
                        {hasServices && hasAdditionalServices && (
                          <hr styleName="divider" />
                        )}
                        {hasAdditionalServices && (
                          <AdditionalStoreServices
                            servicesList={additionalServices}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div styleName="popular-times">
              <PopularTimes
                dates={popularDates}
                onSelectDate={date => {
                  if (date) {
                    setSelectedDate(date);
                  }
                }}
                selectedDate={selectedDate}
                showGraph={!showClosedMessage && !showPartialHoursMessage}
                times={popularTimes}
              />

              {(showClosedMessage || showPartialHoursMessage) && (
                <div styleName="store-closed-message">
                  {selectedDayData?.name}
                  <StoreHolidayMessage day={selectedDayData} />
                </div>
              )}
              {!isArrayWithLength(popularTimes) && !showClosedMessage && (
                <p styleName="store-closed-message">
                  We are still gathering information, but save some time by
                  scheduling an appointment.
                </p>
              )}
            </div>
          </div>

          {!window.isPreRendering && (
            <LazyLoad height={400}>
              <Section
                heading={`${window.siteName} Deals & Rebates`}
                intro={
                  <Typography>
                    We offer deals and rebates on tires and wheels from all of
                    the top brands. Check out these great&nbsp;
                    <Link to="/promotions">deals</Link>!
                  </Typography>
                }
              >
                <StorePromos />
              </Section>
            </LazyLoad>
          )}

          {!window.isPreRendering && (
            <LazyLoad height={500}>
              <Section
                heading={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: overviewHeading
                    }}
                  />
                }
              >
                <div styleName="overview">
                  <div styleName="store-description">
                    {managerName && (
                      <div styleName="manager">
                        <UserSolidIcon styleName="manager-icon" />
                        <span styleName="manager-label">Manager:</span>
                        &nbsp;{managerName}
                      </div>
                    )}

                    <div styleName="overview-copy">
                      <ReadMore
                        contentClassName="store-overview-copy"
                        defaultMaxHeight={305}
                        onClick={() => setReadingMore(!readingMore)}
                      >
                        <div
                          className="cms-content"
                          dangerouslySetInnerHTML={{
                            __html: storeContent
                          }}
                          styleName="overview-copy"
                        />
                      </ReadMore>
                    </div>
                  </div>
                  <div
                    style={{
                      height: readingMore ? 'auto' : null
                    }}
                    styleName="store-map"
                  >
                    {geoPoint?.latitude && geoPoint?.longitude && (
                      <StaticMap
                        centerPos={geoPoint}
                        height={storeMapHeight}
                        isMyStore={isMyStore}
                        width={800}
                        zoom={13}
                      />
                    )}
                  </div>
                </div>
              </Section>
            </LazyLoad>
          )}

          {!window.isPreRendering && (
            <Section
              heading="Customer Reviews"
              id="read-reviews"
              intro={
                <Typography>
                  {rating?.numberOfReviews > 0
                    ? 'Read what our customers have to say.'
                    : 'Be the first to leave a review.'}
                </Typography>
              }
            >
              {rating?.numberOfReviews > 0 && (
                <LazyLoad height={400}>
                  <ReviewOverview
                    averageAnswerRatings={averageAnswerRatings}
                    numberOfReviews={rating?.numberOfReviews}
                    ratingCounts={reviewsSummary}
                    recommendedCount={rating?.recommendedCount}
                    recommendedMessage="people recommend this store"
                    totalRating={rating?.rating}
                  />
                  <StoreReviews storeCode={storeCode} />
                </LazyLoad>
              )}
            </Section>
          )}

          {geoPoint?.latitude && geoPoint?.longitude && storeCode && (
            <LazyLoad height={400}>
              <MoreStores
                isMyStore={isMyStore}
                location={geoPoint}
                nearByMapWidth={nearByMapWidth}
                storeCode={storeCode}
                storeType={storeType}
              />
            </LazyLoad>
          )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

StoreDetailPage.propTypes = {
  router: PropTypes.shape({
    match: PropTypes.shape({
      params: PropTypes.shape({
        storeCode: PropTypes.string
      })
    })
  })
};

export default hot(StoreDetailPage);
